import { useEffect, useMemo, useState } from 'react';
import { Field } from '../../components/ui';
import { formatNumber } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { AmCostLine, Product } from '../../lib/types';

/**
 * The catalogue items a recommendation is proposing.
 *
 * A recommendation used to price itself with a typed number and a sentence.
 * This lets it point at real SKUs from the price book instead, so "deploy MFA"
 * becomes "Cisco DUO MFA for VPN × 40" and the figure comes from the catalogue
 * rather than from memory.
 *
 * WHAT IT DELIBERATELY DOES NOT DO is drive the cost lines by itself. Cost
 * lines are the money and are edited freely — they include labour, rounding
 * and the discount somebody negotiated — while products are a reference to
 * list price. Most quotes are not quantity × list, so the totals here are
 * shown and offered as cost lines on a button, never written silently. Copying
 * them in is one click; discovering the app has overwritten your negotiated
 * price is a phone call to a client.
 */

export interface PickedProduct {
  /** A catalogue product's id; negative for a product typed in (custom). */
  productId: number;
  /** Not in the catalogue: typed in, with the price typed. */
  custom?: boolean;
  quantity: number;
  /* Held for display only. The API re-reads the price from the catalogue when
     it saves — a price sent from a browser is a price a browser chose. */
  name: string;
  unitPrice: number;
  periodType: Product['PeriodType'];
  vendor: string | null;
}

/** What the picked products come to, split the way a quote splits. */
export function productTotals(items: PickedProduct[]) {
  let monthly = 0;
  let annual = 0;
  let oneTime = 0;
  for (const item of items) {
    const line = item.unitPrice * item.quantity;
    if (item.periodType === 'Monthly') monthly += line;
    else if (item.periodType === 'Yearly') annual += line;
    else oneTime += line;
  }
  return { monthly, annual, oneTime };
}

/** The picked products as cost lines, at list price. The starting point for a
 *  quote, not the quote — see the note at the top of this file. */
export function costLinesFromProducts(
  items: PickedProduct[],
  priority: AmCostLine['priority'],
): AmCostLine[] {
  const { monthly, annual, oneTime } = productTotals(items);
  const lines: AmCostLine[] = [];
  if (oneTime !== 0) lines.push({ priority, cost: oneTime, costType: 'One-time' });
  if (monthly !== 0) lines.push({ priority, cost: monthly, costType: 'Monthly' });
  if (annual !== 0) lines.push({ priority, cost: annual, costType: 'Annual' });
  return lines;
}

export function AmProductPicker({
  items,
  onChange,
  onApplyAsCost,
  label = 'From the catalogue',
  hint = 'What this recommendation is proposing they buy. Priced from the price book at list.',
  allowCustom = false,
}: {
  items: PickedProduct[];
  onChange: (next: PickedProduct[]) => void;
  /** Offered only where there are cost lines to write into. */
  onApplyAsCost?: (lines: PickedProduct[]) => void;
  /* The picker is used by the recommendation editor and by the scope of work
     builder, which are asking different questions of the same catalogue: what
     the client should buy, and what the engineer needs to do the job. */
  label?: string;
  hint?: string;
  /** Offer "Add a product not in the catalogue" — typed in with its own price. */
  allowCustom?: boolean;
}) {
  const [term, setTerm] = useState('');
  const [customOpen, setCustomOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  /* Debounced for the same reason the catalogue page debounces: the search
     runs across three columns server-side. */
  useEffect(() => {
    const timer = setTimeout(() => setQuery(term.trim()), 220);
    return () => clearTimeout(timer);
  }, [term]);

  /* Nothing is fetched until somebody types. The catalogue is 304 rows and
     this is a dialog inside a dialog — loading it to show an empty box would
     be a round trip for a list nobody has asked to see yet. */
  const results = useResource<Product[]>(
    open && query.length >= 2 ? `/products?q=${encodeURIComponent(query)}` : null,
  );

  const picked = new Set(items.map((i) => i.productId));
  const totals = useMemo(() => productTotals(items), [items]);

  const add = (product: Product) => {
    if (picked.has(product.ProductId)) return;
    onChange([
      ...items,
      {
        productId: product.ProductId,
        quantity: 1,
        name: product.Name,
        unitPrice: product.UnitPrice,
        periodType: product.PeriodType,
        vendor: product.Vendor,
      },
    ]);
    setTerm('');
  };

  const setQuantity = (productId: number, quantity: number) =>
    onChange(items.map((i) => (i.productId === productId ? { ...i, quantity } : i)));
  const setItem = (productId: number, patch: Partial<PickedProduct>) =>
    onChange(items.map((i) => (i.productId === productId ? { ...i, ...patch } : i)));

  const parts = [
    totals.oneTime !== 0 && `${formatNumber(totals.oneTime, 'currency')} one-time`,
    totals.monthly !== 0 && `${formatNumber(totals.monthly, 'currency')}/mo`,
    totals.annual !== 0 && `${formatNumber(totals.annual, 'currency')}/yr`,
  ].filter((p): p is string => Boolean(p));

  return (
    <Field label={label} hint={hint}>
      {items.length > 0 && (
        <div className="am-product-list">
          {items.map((item) => (
            <div key={item.productId} className="am-product-line">
              {item.custom ? (
                <div className="grow am-custom-line">
                  <input
                    className="input input-sm"
                    value={item.name}
                    aria-label="Product name"
                    onChange={(e) => setItem(item.productId, { name: e.target.value })}
                  />
                  <div className="row wrap" style={{ gap: 'var(--space-1)', marginTop: 4 }}>
                    <input
                      className="input input-sm"
                      style={{ width: 140 }}
                      value={item.vendor ?? ''}
                      placeholder="Vendor (optional)"
                      aria-label="Vendor"
                      onChange={(e) => setItem(item.productId, { vendor: e.target.value || null })}
                    />
                    <input
                      className="input input-sm"
                      style={{ width: 100 }}
                      type="number"
                      step="0.01"
                      value={item.unitPrice}
                      aria-label="Unit price"
                      onChange={(e) => setItem(item.productId, { unitPrice: Number(e.target.value) || 0 })}
                    />
                    <select
                      className="select input-sm"
                      value={item.periodType}
                      aria-label="Bills"
                      onChange={(e) => setItem(item.productId, { periodType: e.target.value as Product['PeriodType'] })}
                    >
                      <option value="One-time">One-time</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Yearly">Yearly</option>
                    </select>
                    <span className="xs subtle" style={{ alignSelf: 'center' }}>
                      not in the catalogue
                    </span>
                  </div>
                </div>
              ) : (
                <div className="grow">
                  <div className="small strong">{item.name}</div>
                  <div className="xs subtle">
                    {[item.vendor, `${formatNumber(item.unitPrice, 'currency')} ${
                      item.periodType === 'One-time' ? 'one-time' : item.periodType.toLowerCase()
                    }`]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
              )}
              <input
                className="input"
                style={{ width: 76 }}
                type="number"
                min="0.01"
                step="1"
                value={item.quantity}
                aria-label={`Quantity of ${item.name}`}
                onChange={(e) => setQuantity(item.productId, Number(e.target.value) || 1)}
              />
              <span className="mono small">
                {formatNumber(item.unitPrice * item.quantity, 'currency')}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-icon btn-sm"
                title={`Remove ${item.name}`}
                onClick={() => onChange(items.filter((i) => i.productId !== item.productId))}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && parts.length > 0 && (
        <div className="row-between wrap mt-2">
          <span className="xs muted">At list: {parts.join(' + ')}</span>
          {onApplyAsCost && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => onApplyAsCost(items)}
            >
              Use as the price
            </button>
          )}
        </div>
      )}

      {open ? (
        <div className="am-product-search mt-2">
          <input
            className="input"
            value={term}
            autoFocus
            placeholder="Search the catalogue — name, description or vendor"
            onChange={(e) => setTerm(e.target.value)}
            aria-label="Search the product catalogue"
          />
          {query.length >= 2 && (
            <div className="am-product-results">
              {results.loading && <p className="xs subtle">Searching…</p>}
              {!results.loading && (results.data ?? []).length === 0 && (
                <p className="xs subtle">Nothing in the catalogue matches "{query}".</p>
              )}
              {(results.data ?? []).slice(0, 12).map((p) => (
                <button
                  key={p.ProductId}
                  type="button"
                  className="am-product-result"
                  disabled={picked.has(p.ProductId)}
                  onClick={() => add(p)}
                >
                  <span className="grow">
                    <span className="small">{p.Name}</span>
                    <span className="xs subtle">
                      {[p.Vendor, p.Category].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className="mono xs">
                    {formatNumber(p.UnitPrice, 'currency')}
                    {p.PeriodType === 'One-time' ? '' : p.PeriodType === 'Yearly' ? '/yr' : '/mo'}
                  </span>
                </button>
              ))}
            </div>
          )}
          <button type="button" className="btn btn-ghost btn-sm mt-2" onClick={() => setOpen(false)}>
            Done
          </button>
        </div>
      ) : (
        <div className="row wrap mt-2" style={{ gap: 'var(--space-2)' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
            + Add from the catalogue
          </button>
          {allowCustom && !customOpen && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCustomOpen(true)}>
              + Add a product not in the catalogue
            </button>
          )}
        </div>
      )}
      {allowCustom && customOpen && (
        <CustomProductForm
          onCancel={() => setCustomOpen(false)}
          onAdd={(p) => {
            onChange([...items, { ...p, productId: -Date.now(), custom: true }]);
            setCustomOpen(false);
          }}
        />
      )}
    </Field>
  );
}

/** A product the catalogue does not carry: typed in, with its own price. */
function CustomProductForm({
  onAdd,
  onCancel,
}: {
  onAdd: (p: Omit<PickedProduct, 'productId' | 'custom'>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [vendor, setVendor] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');
  const [periodType, setPeriodType] = useState<Product['PeriodType']>('One-time');
  const qty = Number(quantity);
  const price = Number(unitPrice);
  const ok = name.trim() !== '' && qty > 0 && unitPrice.trim() !== '' && Number.isFinite(price);
  return (
    <div className="am-custom-product mt-2">
      <div className="small strong mb-2">A product not in the catalogue</div>
      <div className="am-custom-grid">
        <label>
          <span className="xs muted">Product name</span>
          <input className="input" value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="What it is" />
        </label>
        <label>
          <span className="xs muted">Vendor (optional)</span>
          <input className="input" value={vendor} onChange={(e) => setVendor(e.target.value)} />
        </label>
        <label>
          <span className="xs muted">Quantity</span>
          <input className="input" type="number" min="0.01" step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </label>
        <label>
          <span className="xs muted">Unit price</span>
          <input className="input" type="number" step="0.01" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} placeholder="0.00" />
        </label>
        <label>
          <span className="xs muted">Bills</span>
          <select className="select" value={periodType} onChange={(e) => setPeriodType(e.target.value as Product['PeriodType'])}>
            <option value="One-time">One-time</option>
            <option value="Monthly">Monthly</option>
            <option value="Yearly">Yearly</option>
          </select>
        </label>
      </div>
      <div className="row mt-2" style={{ gap: 'var(--space-2)' }}>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={!ok}
          onClick={() => onAdd({ name: name.trim(), vendor: vendor.trim() || null, quantity: qty, unitPrice: price, periodType })}
        >
          Add product
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
